import { useEffect, useState, useCallback } from "react";
import api, { formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wallet, CalendarDays, Plus, Trash2, ArrowDownCircle, ArrowUpCircle, Save, Landmark, Download, Paperclip, Receipt } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return `${MONTHS[Number(mo) - 1]} ${y}`;
};
const CATEGORIES = ["ATK", "Transport/Bensin", "Tol & Parkir", "Konsumsi", "Galon", "Kebersihan", "Iuran Keamanan", "Lembur", "Uang Tips", "Top Up / Setoran", "Lainnya"];

const Stat = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay }}
    className="rounded-2xl border border-brand-line bg-white p-5 shadow-[0_8px_30px_rgba(45,35,30,0.05)]"
    data-testid={`petty-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <p className="text-sm font-medium text-brand-muted">{label}</p>
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent || "bg-brand-gold/15 text-brand-gold"}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <p className="mt-3 font-serif text-2xl font-bold text-brand-espresso">{formatIDR(value)}</p>
  </motion.div>
);

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PettyCash() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [openingInput, setOpeningInput] = useState("");
  const [savingOpening, setSavingOpening] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), type: "out", category: "ATK", description: "", amount: "" });
  const [adding, setAdding] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/petty-cash", { params: { month } })
      .then(({ data }) => {
        setData(data);
        setOpeningInput(data.opening_source === "manual" ? String(data.opening ?? "") : "");
      })
      .catch(() => setData(null));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const saveOpening = async () => {
    setSavingOpening(true);
    try {
      await api.put("/petty-cash/opening", { month, amount: Number(openingInput) || 0 });
      toast.success("Saldo awal tersimpan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan saldo awal");
    } finally { setSavingOpening(false); }
  };

  const addEntry = async () => {
    if (!form.date) { toast.error("Tanggal wajib diisi"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Jumlah harus lebih dari 0"); return; }
    setAdding(true);
    try {
      let receipt_path = null;
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        const { data } = await api.post("/petty-cash/receipt", fd);
        receipt_path = data.receipt_path;
      }
      await api.post("/petty-cash", {
        date: form.date, type: form.type, category: form.category,
        description: form.description, amount: Number(form.amount) || 0, receipt_path,
      });
      toast.success("Transaksi ditambahkan");
      setForm((f) => ({ ...f, description: "", amount: "" }));
      setReceiptFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah transaksi");
    } finally { setAdding(false); }
  };

  const deleteEntry = async (id) => {
    try {
      await api.delete(`/petty-cash/${id}`);
      toast.success("Transaksi dihapus");
      load();
    } catch (e) {
      toast.error("Gagal menghapus transaksi");
    }
  };

  const doExport = async () => {    try {
      const res = await api.get("/petty-cash/export", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buku-kas-${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengekspor");
    }
  };

  const viewReceipt = async (path) => {
    try {
      const res = await api.get(`/petty-cash/receipt/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setReceiptUrl(url);
      setReceiptOpen(true);
    } catch (e) {
      toast.error("Gagal memuat struk");
    }
  };

  const needsBranch = data?.needs_branch;
  const entries = data?.entries || [];
  let running = Number(data?.opening) || 0;
  const rowsWithBalance = entries.map((e) => {
    running += e.type === "in" ? e.amount : -e.amount;
    return { ...e, balance: running };
  });

  return (
    <div className="p-6 md:p-10" data-testid="petty-cash-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Buku Kas Harian</p>
          <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Laporan Keuangan</h1>
          <p className="mt-1 text-sm text-brand-muted">Petty cash & pengeluaran kantor harian per cabang.</p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <div className="flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-1.5">
            <CalendarDays className="h-4 w-4 text-brand-muted" />
            <span className="text-sm text-brand-muted">Bulan:</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="petty-month" className="border-0 p-0 text-sm text-brand-espresso focus:outline-none" />
          </div>
          {!needsBranch && (
            <Button onClick={doExport} variant="outline" data-testid="petty-export-btn" className="gap-2 border-brand-line text-brand-espresso hover:bg-brand-surface">
              <Download className="h-4 w-4" /> Ekspor Excel
            </Button>
          )}
        </div>
      </div>

      {needsBranch ? (
        <div className="rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center" data-testid="petty-needs-branch">
          <Landmark className="mx-auto h-10 w-10 text-brand-muted" />
          <p className="mt-3 font-serif text-lg font-bold text-brand-espresso">Pilih Cabang Aktif</p>
          <p className="mt-1 text-sm text-brand-muted">Buku kas bersifat per cabang. Silakan pilih cabang pada dropdown "Cabang Aktif" di sidebar.</p>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Wallet} label="Saldo Awal" value={data?.opening} delay={0} />
            <Stat icon={ArrowDownCircle} label="Total Pemasukan" value={data?.total_in} accent="bg-green-100 text-green-700" delay={0.05} />
            <Stat icon={ArrowUpCircle} label="Total Pengeluaran" value={data?.total_out} accent="bg-red-100 text-red-600" delay={0.1} />
            <Stat icon={Landmark} label="Saldo Akhir" value={data?.closing} accent="bg-brand-espresso text-brand-gold" delay={0.15} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Opening + Add form */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="petty-opening-card">
                <h2 className="mb-1 font-serif text-lg font-bold text-brand-espresso">Saldo Awal Bulan</h2>
                <p className="mb-3 text-xs text-brand-muted">
                  {data?.opening_source === "manual"
                    ? "Diatur manual untuk bulan ini."
                    : `Otomatis dari saldo akhir bulan sebelumnya (${formatIDR(data?.opening)}). Isi untuk menimpa.`}
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex flex-1 items-center gap-1 rounded-md border border-brand-line px-2 py-2">
                    <span className="text-xs text-brand-muted">Rp</span>
                    <input type="number" value={openingInput} placeholder={String(data?.opening ?? 0)}
                      onChange={(e) => setOpeningInput(e.target.value)}
                      data-testid="petty-opening-input" className="w-full border-0 p-0 text-right text-sm focus:outline-none" />
                  </div>
                  <Button onClick={saveOpening} disabled={savingOpening} data-testid="petty-opening-save" className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
                    <Save className="h-4 w-4" /> {savingOpening ? "..." : "Simpan"}
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="petty-add-card">
                <h2 className="mb-4 font-serif text-lg font-bold text-brand-espresso">Tambah Transaksi</h2>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Tanggal</label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="petty-form-date" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Jenis</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setForm({ ...form, type: "out" })} data-testid="petty-form-type-out"
                        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.type === "out" ? "border-red-500 bg-red-50 text-red-600" : "border-brand-line text-brand-muted hover:bg-brand-surface"}`}>
                        Pengeluaran
                      </button>
                      <button type="button" onClick={() => setForm({ ...form, type: "in" })} data-testid="petty-form-type-in"
                        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.type === "in" ? "border-green-600 bg-green-50 text-green-700" : "border-brand-line text-brand-muted hover:bg-brand-surface"}`}>
                        Pemasukan
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Kategori</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="petty-form-category"
                      className="w-full rounded-md border border-brand-line px-3 py-2 text-sm text-brand-espresso focus:outline-none focus:ring-2 focus:ring-brand-gold">
                      {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Keterangan</label>
                    <Input value={form.description} placeholder="Contoh: Beli kertas HVS" onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="petty-form-desc" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Jumlah</label>
                    <div className="flex items-center gap-1 rounded-md border border-brand-line px-2 py-2">
                      <span className="text-xs text-brand-muted">Rp</span>
                      <input type="number" value={form.amount} placeholder="0" onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        data-testid="petty-form-amount" className="w-full border-0 p-0 text-right text-sm focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-brand-muted">Foto Struk (opsional)</label>
                    <label htmlFor="petty-receipt-file" data-testid="petty-form-receipt" className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-brand-line px-3 py-2 text-sm text-brand-muted transition-colors hover:bg-brand-surface">
                      <Paperclip className="h-4 w-4 text-brand-gold" />
                      <span className="truncate">{receiptFile ? receiptFile.name : "Pilih foto struk (JPG/PNG, maks 5MB)"}</span>
                    </label>
                    <input id="petty-receipt-file" type="file" accept="image/*" className="hidden"
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                  </div>
                  <Button onClick={addEntry} disabled={adding} data-testid="petty-form-submit" className="w-full gap-2 bg-brand-espresso text-white hover:bg-brand-espresso/90">
                    <Plus className="h-4 w-4" /> {adding ? "Menambah..." : "Tambah Transaksi"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
                <table className="w-full text-sm" data-testid="petty-table">
                  <thead>
                    <tr className="border-b border-brand-line bg-brand-surface/40 text-left text-xs uppercase tracking-wide text-brand-muted">
                      <th className="px-4 py-3 font-semibold">Tanggal</th>
                      <th className="px-4 py-3 font-semibold">Kategori</th>
                      <th className="px-4 py-3 font-semibold">Keterangan</th>
                      <th className="px-4 py-3 text-right font-semibold">Masuk</th>
                      <th className="px-4 py-3 text-right font-semibold">Keluar</th>
                      <th className="px-4 py-3 text-right font-semibold">Saldo</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-brand-line/60 bg-brand-surface/20 font-medium text-brand-espresso">
                      <td className="px-4 py-2" colSpan={5}>Saldo Awal ({monthLabel(month)})</td>
                      <td className="px-4 py-2 text-right">{formatIDR(data?.opening)}</td>
                      <td />
                    </tr>
                    {rowsWithBalance.length === 0 && (
                      <tr><td className="px-4 py-8 text-center text-brand-muted" colSpan={7}>Belum ada transaksi pada bulan ini.</td></tr>
                    )}
                    {rowsWithBalance.map((r) => (
                      <tr key={r.id} className="border-b border-brand-line/40 hover:bg-brand-surface/30" data-testid={`petty-row-${r.id}`}>
                        <td className="px-4 py-2 text-brand-muted">{r.date}</td>
                        <td className="px-4 py-2">
                          <span className="rounded-full bg-brand-gold/15 px-2 py-0.5 text-xs font-medium text-brand-espresso">{r.category || "-"}</span>
                        </td>
                        <td className="px-4 py-2 text-brand-espresso">{r.description || "-"}</td>
                        <td className="px-4 py-2 text-right text-green-700">{r.type === "in" ? formatIDR(r.amount) : "-"}</td>
                        <td className="px-4 py-2 text-right text-red-600">{r.type === "out" ? formatIDR(r.amount) : "-"}</td>
                        <td className="px-4 py-2 text-right font-medium text-brand-espresso">{formatIDR(r.balance)}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {r.receipt_path && (
                              <button onClick={() => viewReceipt(r.receipt_path)} data-testid={`petty-receipt-${r.id}`} title="Lihat struk" className="text-brand-gold hover:text-brand-espresso">
                                <Receipt className="h-4 w-4" />
                              </button>
                            )}
                            <button onClick={() => deleteEntry(r.id)} data-testid={`petty-del-${r.id}`} className="text-brand-muted hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-brand-line bg-brand-espresso font-bold text-white">
                      <td className="px-4 py-3" colSpan={3}>Saldo Akhir ({monthLabel(month)})</td>
                      <td className="px-4 py-3 text-right text-green-300">{formatIDR(data?.total_in)}</td>
                      <td className="px-4 py-3 text-right text-red-300">{formatIDR(data?.total_out)}</td>
                      <td className="px-4 py-3 text-right text-brand-gold">{formatIDR(data?.closing)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="mt-3 text-xs text-brand-muted">
                Total pengeluaran ({formatIDR(data?.total_out)}) otomatis menjadi sumber "Pengeluaran Kantor" bulanan pada Laporan Keuangan bagian Keuangan.
              </p>
            </div>
          </div>
        </>
      )}

      <Dialog open={receiptOpen} onOpenChange={(o) => { setReceiptOpen(o); if (!o && receiptUrl) { URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); } }}>
        <DialogContent className="max-w-lg" data-testid="petty-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Bukti Struk</DialogTitle>
          </DialogHeader>
          {receiptUrl && <img src={receiptUrl} alt="Struk" className="max-h-[70vh] w-full rounded-md object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
